import styled, { css, keyframes } from "styled-components";
import { color } from "../../../shared/styles";
import { Backdrop, Button, TableCell, tableCellClasses, TableRow } from "@mui/material";
export const MaterialTableText = styled.div`
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    border-left: 5px solid var(--Primary-Blue-B100, #0058a0);
    padding-left: 15px;
`;
export const MaterialOrderLinkedWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;
export const MaterialOrderChildLinkedWrapper = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
`;
export const MaterialOrderLinkedTitle = styled.div`
    color: #333739;
    text-align: left;
    font-family: "Noto Sans";
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px; /* 150% */
`

export const HeaderMeta = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

export const DueDateBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 26px;
    padding: 2px 10px;
    border: 1px solid ${color.secondary.BORDER};
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    color: ${color.secondary.CHARCOAL};
    background-color: ${color.emphasis.NEUTRAL_N0};
`;

export const UrgentBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 26px;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 14px; /* Bigger text as requested */
    font-weight: 700;
    line-height: 18px;
    color: ${color.status.WARNING};
    background-color: ${color.status.WARNING_LIGHT};
    border: 1px solid ${color.status.WARNING};
`;

export const HeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const MetaRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

export const IconWrap = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    & > svg {
        width: 16px;
        height: 16px;
        display: block;
    }
`;

export const BadgeLabel = styled.span`
    font-weight: 600;
    font-size: 12px;
    color: ${color.secondary.DARK_GREY};
`;

export const BadgeValue = styled.span`
    font-weight: 700;
    font-size: 14px;
    color: ${color.primary.BLUE_B100};
`;

export const DividerDot = styled.span`
    width: 4px;
    height: 4px;
    border-radius: 999px;
    background: ${color.secondary.GREY_4};
    display: inline-block;
`;

// Minimal meta item (shadcn-inspired): icon + small label + clear value
export const MetaItem = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

export const MetaLabel = styled.span`
    font-size: 12px;
    color: ${color.secondary.DARK_GREY};
    text-transform: uppercase;
    letter-spacing: 0.02em;
`;

export const MetaValue = styled.span`
    font-size: 15px;
    font-weight: 600;
    color: ${color.secondary.CHARCOAL};
`;

export const UrgentDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: ${color.status.WARNING};
    display: inline-block;
`;

export const UrgentText = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${color.status.WARNING};
`;

export const MetaBar = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 6px 8px;
    border-radius: 6px;
    border: 1px solid ${color.secondary.BORDER};
    background: ${color.emphasis.NEUTRAL_N5};
    transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
    will-change: transform, box-shadow;

    &:hover {
        background: ${color.emphasis.NEUTRAL_N0};
        border-color: ${color.secondary.GREY_4};
        box-shadow: 0 2px 10px rgba(16, 47, 67, 0.12);
        transform: translateY(-2px);
    }

    &:active {
        transform: translateY(-1px);
        box-shadow: 0 1px 6px rgba(16, 47, 67, 0.12);
    }

    &:focus-within {
        outline: none;
        border-color: ${color.primary.BLUE_B100};
        box-shadow: 0 0 0 2px rgba(0, 88, 160, 0.12), 0 2px 10px rgba(16, 47, 67, 0.12);
        background: ${color.emphasis.NEUTRAL_N0};
    }
`;

export const MetaDividerV = styled.span`
    width: 1px;
    height: 16px;
    background: ${color.secondary.BORDER};
    display: inline-block;
`;

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translate(-50%, -60%);
    }
    to {
        opacity: 1;
        transform: translate(-50%, -50%);
    }
`;

const fadeOut = keyframes`
    from {
        opacity: 1;
        transform: translate(-50%, -50%);
    }
    to {
        opacity: 0;
        transform: translate(-50%, -60%);
    }
`;

export const ModalWrapper = styled.div<{ isOpen: boolean }>`
    padding: 24px;
    max-width: 80vw;
    width: 70vw;
    min-width: 500px;
    max-height: 85vh;
    border-radius: 8px;
    background-color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 24px;
    font-family: Noto Sans;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    animation: ${({ isOpen }) =>
        isOpen
            ? css`
                  ${fadeIn} 0.3s ease-out forwards
              `
            : css`
                  ${fadeOut} 0.3s ease-out forwards
              `};
`;

export const Expandable = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    overflow-y: auto;
`;

export const TableWrapper = styled.div`
    overflow-y: auto;
`;

export const TitleWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

export const InfoText = styled.div`
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
`;

export const TitleText = styled.div`
    font-size: 22px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px;
`;

export const TitleTextList = styled.div`
    display: flex;
    font-size: 22px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px;
    border-left: 5px solid var(--Primary-Blue-B100, #0058a0);
    padding-left: 15px;
    text-align: center;
`;

export const SummaryTableRow = styled(TableRow)`
    background-color: ${color.secondary.LIGHT_GREY};
    position: sticky;
    z-index: 10;
    bottom: 0;
`;

export const SummaryTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.body}`]: {
        color: "#0D0C0C",
        fontSize: "14px",
        fontWeight: "700",
        lineHeight: "42px",
        wordWrap: "break-word",
        padding: "8px 8px 8px 8px",
        width: "171px",
        minWidth: "171px",
        fontFamily: "Noto Sans",
        whiteSpace: "nowrap",
    },
}));

export const ButtonRow = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 8px;
    margin-top: auto;
`;

export const StyledButton = styled(Button)`
    && {
        width: 100%;
        height: 51px;
        font-weight: 700;
        font-size: 16px;
        line-height: 22px;
    }
`;

export const StyledBackdrop = styled(Backdrop)`
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(5px);
`;

export const StyledModalButton = styled(Button)`
    && {
        padding-top: 8px;
        padding-bottom: 8px;
        padding-left: 14px;
        padding-right: 14px;
        display: flex;
        justify-content: center;
        gap: 4px;
        font-size: 14px;
        height: 36px;
        border-radius: 5px;
        &.MuiButton-outlined {
            border-color: var(--Primary-Blue-B100, #0058a0);
            color: var(--Primary-Blue-B100, #0058a0);
        }
    }
`;

export const ManufacturingOrderLine = styled.button`
    width: 100%;
    min-height: 36px;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    border: 1px solid ${color.secondary.BORDER};
    border-radius: 6px;
    background: ${color.emphasis.NEUTRAL_N0};
    cursor: pointer;
    font-family: "Noto Sans";
    text-align: left;
    transition: border-color 0.15s ease, background-color 0.15s ease;

    &:hover {
        border-color: ${color.primary.BLUE_B100};
        background: ${color.emphasis.NEUTRAL_N5};
    }
`;

export const ManufacturingOrderLineLabel = styled.span`
    flex: 0 0 auto;
    font-size: 13px;
    font-weight: 700;
    color: ${color.secondary.DARK_GREY};
`;

export const ManufacturingOrderLineValue = styled.span`
    min-width: 0;
    font-size: 14px;
    font-weight: 700;
    color: ${color.primary.BLUE_B100};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;
